import React, { useState, useEffect } from 'react';
import { Bus, Booking } from '../../types';

interface BusSeatSelectionProps {
  bus: Bus;
  existingBookings?: Booking[];
  selectedSeats: { [passengerIndex: number]: string };
  onSeatSelect: (passengerIndex: number, seatNumber: string) => void;
  passengers: Array<{
    name: string;
    isMainContact?: boolean;
  }>;
}

const BusSeatSelection: React.FC<BusSeatSelectionProps> = ({
  bus,
  existingBookings = [],
  selectedSeats,
  onSeatSelect,
  passengers
}) => {
  const [reservedSeats, setReservedSeats] = useState<Set<string>>(new Set());

  useEffect(() => {
    // Get reserved seats from existing bookings
    const reserved = new Set<string>();
    existingBookings.forEach(booking => {
      if (booking.assignedBus?._id === bus._id) {
        booking.companions?.forEach(companion => {
          if (companion.seatNumber) {
            reserved.add(companion.seatNumber);
          }
        });
        // Add contact person seat if assigned
        if (booking.contactPerson && 'seatNumber' in booking.contactPerson) {
          const contactSeat = (booking.contactPerson as any).seatNumber;
          if (contactSeat) {
            reserved.add(contactSeat);
          }
        }
      }
    });
    setReservedSeats(reserved);
  }, [existingBookings, bus._id]);

  const generateSeats = () => {
    const seats = [];
    const capacity = bus.capacity || 49; // Default to 49 seats like in the image
    
    // Create seat layout similar to the Arabic form
    for (let i = 1; i <= capacity; i++) {
      seats.push(i.toString());
    }
    return seats;
  };

  const seats = generateSeats();

  const getSeatStatus = (seatNumber: string) => {
    if (reservedSeats.has(seatNumber)) {
      return 'reserved';
    }
    
    // Check if this seat is selected by any passenger
    const selectedByPassenger = Object.entries(selectedSeats).find(
      ([_, seat]) => seat === seatNumber
    );
    
    if (selectedByPassenger) {
      return 'selected';
    }
    
    return 'available';
  };

  const getSeatClass = (seatNumber: string) => {
    const status = getSeatStatus(seatNumber);
    const baseClass = "w-10 h-10 border-2 rounded-lg flex items-center justify-center text-sm font-medium cursor-pointer transition-all duration-200 hover:scale-105";
    
    switch (status) {
      case 'reserved':
        return `${baseClass} bg-red-100 border-red-300 text-red-700 cursor-not-allowed`;
      case 'selected':
        return `${baseClass} bg-blue-500 border-blue-600 text-white shadow-lg`;
      case 'available':
        return `${baseClass} bg-green-100 border-green-300 text-green-700 hover:bg-green-200`;
      default:
        return baseClass;
    }
  };

  const handleSeatClick = (seatNumber: string) => {
    const status = getSeatStatus(seatNumber);
    if (status === 'reserved') return;

    // Find which passenger (if any) currently has this seat
    const currentPassengerIndex = Object.entries(selectedSeats).find(
      ([_, seat]) => seat === seatNumber
    )?.[0];

    if (currentPassengerIndex) {
      // Seat is already selected, deselect it
      onSeatSelect(parseInt(currentPassengerIndex), '');
    } else {
      // Find first passenger without a seat
      const unassignedPassenger = passengers.findIndex((_, index) => !selectedSeats[index]);
      if (unassignedPassenger !== -1) {
        onSeatSelect(unassignedPassenger, seatNumber);
      }
    }
  };

  const getPassengerForSeat = (seatNumber: string) => {
    const passengerIndex = Object.entries(selectedSeats).find(
      ([_, seat]) => seat === seatNumber
    )?.[0];
    
    if (passengerIndex) {
      return passengers[parseInt(passengerIndex)];
    }
    return null;
  };

  const renderSeatGrid = () => {
    const seatsPerRow = 4; // 2 seats on each side of aisle
    const rows = Math.ceil(seats.length / seatsPerRow);
    const seatRows = [];

    for (let row = 0; row < rows; row++) {
      const rowSeats = [];
      const startIndex = row * seatsPerRow;
      
      for (let col = 0; col < seatsPerRow; col++) {
        const seatIndex = startIndex + col;
        if (seatIndex < seats.length) {
          const seatNumber = seats[seatIndex];
          const passenger = getPassengerForSeat(seatNumber);
          
          rowSeats.push(
            <div key={seatNumber} className="flex flex-col items-center">
              <button
                type="button"
                className={getSeatClass(seatNumber)}
                onClick={() => handleSeatClick(seatNumber)}
                disabled={getSeatStatus(seatNumber) === 'reserved'}
                title={
                  passenger 
                    ? `${seatNumber} - ${passenger.name}` 
                    : getSeatStatus(seatNumber) === 'reserved' 
                      ? `مقعد ${seatNumber} - محجوز`
                      : `مقعد ${seatNumber} - متاح`
                }
              >
                {seatNumber}
              </button>
              {passenger && (
                <div className="text-xs text-gray-600 mt-1 text-center max-w-12 truncate">
                  {passenger.name.split(' ')[0]}
                </div>
              )}
            </div>
          );
        }
        
        // Add aisle space after 2nd seat
        if (col === 1 && seatIndex < seats.length - 1) {
          rowSeats.push(
            <div key={`aisle-${row}`} className="w-6 flex items-center justify-center">
              <div className="w-1 h-8 bg-gray-300 rounded"></div>
            </div>
          );
        }
      }
      
      // Add driver above seats 3 and 4 (first row: seats 1-4)
      const rowStartSeat = startIndex + 1; // Convert to 1-based seat numbering
      const rowEndSeat = Math.min(startIndex + seatsPerRow, seats.length);
      const isFirstRow = (rowStartSeat <= 4 && rowEndSeat >= 1);
      
      // Add toilet and back door after the row containing seats 17 and 18
      const containsSeats17Or18 = (rowStartSeat <= 18 && rowEndSeat >= 17);

      if (isFirstRow) {
        seatRows.push(
          <div key={row} className="mb-3">
            <div className="flex justify-end mb-1 ml-60">
            <div className="bg-blue-100 border border-blue-300 rounded px-3 py-1 text-xs text-blue-700 ml-9">
                الباب الأمامي
              </div>
              <div className="bg-gray-100 border-2 border-gray-300 rounded-lg px-3 ml-4 py-1 text-sm font-medium text-gray-700">
                السائق
              </div>
            </div>
            <div className="flex items-center justify-center gap-2">
              {rowSeats}
            </div>
          </div>
        );
      } else if (containsSeats17Or18) {
        seatRows.push(
          <div key={row} className="mb-1">
            <div className="flex items-center justify-center gap-2 mb-1">
              {rowSeats}
            </div>
            <div className="flex justify-center">
              <div className="flex flex-col items-center gap-1">
                <div className="bg-blue-100 border border-blue-300 rounded px-2 py-1 text-xs text-blue-700 flex items-center">
                  🚽 دورة المياه
                </div>
                <div className="bg-blue-100 border border-blue-300 rounded px-2 py-1 text-xs text-blue-700 flex items-center">
                  🚪 الباب الخلفي
                </div>
              </div>
            </div>
          </div>
        );
      } else {
        seatRows.push(
          <div key={row} className="flex items-center justify-center gap-2 mb-3">
            {rowSeats}
          </div>
        );
      }
    }

    return seatRows;
  };

  return (
    <div className="bg-white rounded-lg border border-gray-200 p-6" dir="rtl">
      {/* Header */}
      <div className="text-center mb-6">
        <h3 className="text-lg font-bold text-gray-900 mb-2">
          اختيار المقاعد - حافلة {bus.busNumber}
        </h3>
        <div className="text-sm text-gray-600">
          السعة: {bus.capacity} مقعد | المتاح: {seats.length - reservedSeats.size} مقعد
        </div>
      </div>





      {/* Seat Grid */}
      <div className="bg-gray-50 rounded-lg p-4 mb-6">
        {renderSeatGrid()}
      </div>



      {/* Legend */}
      <div className="grid grid-cols-3 gap-4 text-sm">
        <div className="flex items-center">
          <div className="w-4 h-4 bg-green-100 border border-green-300 rounded ml-2"></div>
          <span>متاح</span>
        </div>
        <div className="flex items-center">
          <div className="w-4 h-4 bg-blue-500 border border-blue-600 rounded ml-2"></div>
          <span>محدد</span>
        </div>
        <div className="flex items-center">
          <div className="w-4 h-4 bg-red-100 border border-red-300 rounded ml-2"></div>
          <span>محجوز</span>
        </div>
      </div>

      {/* Passenger assignments */}
      {passengers.length > 0 && (
        <div className="mt-6 border-t pt-4">
          <h4 className="font-medium text-gray-900 mb-3">تخصيص المقاعد:</h4>
          <div className="space-y-2">
            {passengers.map((passenger, index) => (
              <div key={index} className="flex items-center justify-between bg-gray-50 rounded-lg p-3">
                <div className="flex items-center">
                  <span className="text-sm font-medium text-gray-900">
                    {passenger.name}
                  </span>
                  {passenger.isMainContact && (
                    <span className="mr-2 bg-blue-100 text-blue-800 text-xs px-2 py-1 rounded">
                      المسؤول
                    </span>
                  )}
                </div>
                <div className="text-sm text-gray-600">
                  {selectedSeats[index] ? (
                    <span className="bg-green-100 text-green-800 px-2 py-1 rounded">
                      مقعد {selectedSeats[index]}
                    </span>
                  ) : (
                    <span className="text-orange-600">لم يتم التخصيص</span>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default BusSeatSelection; 